// =============================================================
// Shared UI primitives — ported from scripts/perpetuum-mobile-lib.jsx.
// Icon · Btn · StatusChip · OverdueBar. These back every surface and keep the
// prototype's class contract (.btn, .chip-status, .overdue-bar) so the design
// system carries over verbatim. Colour comes from CSS vars (token bridge).
// =============================================================

import type { CSSProperties, ReactNode } from "react";
import type { StatusKind } from "@perpetuum/core";
import { classifyTolerance, relativeOverdue } from "@perpetuum/core";
import type { RecordSyncState } from "../app/SyncContext.js";
import { useScopedT } from "../lib/i18n-keys.js";

/** Stroke-icon set used across the console (single-path 24×24 viewBox). */
export const ICONS = {
  x: "M18 6 6 18M6 6l12 12",
  plus: "M12 5v14M5 12h14",
  check: "M20 6 9 17l-5-5",
  chevron: "M9 18l6-6-6-6",
  chevronDown: "M6 9l6 6 6-6",
  wrench: "M14.7 6.3a4 4 0 0 0-5.2 5.2L3 18l3 3 6.5-6.5a4 4 0 0 0 5.2-5.2l-2.4 2.4-2.8-.7-.7-2.8z",
  gauge: "M12 14l4-4M3.3 16a9 9 0 1 1 17.4 0M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z",
  down: "M10.3 3.1a2 2 0 0 1 3.4 0l8.4 14a2 2 0 0 1-1.7 3H3.6a2 2 0 0 1-1.7-3zM12 9v4M12 17h.01",
  file: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6",
  box: "M21 8 12 3 3 8v8l9 5 9-5V8zM3 8l9 5 9-5M12 13v8",
  history: "M3 3v5h5M3.05 13A9 9 0 1 0 6 5.3L3 8M12 7v5l4 2",
  clock: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2",
  tree: "M3 3h7v7H3zM14 14h7v7h-7zM7 10v4h7M14 17H7",
  inventory: "M21 8 12 3 3 8v8l9 5 9-5V8zM3 8l9 5 9-5",
  dashboard: "M3 3h8v8H3zM13 3h8v5h-8zM13 12h8v9h-8zM3 14h8v7H3z",
  users: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
  shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  alert: "M10.3 3.1a2 2 0 0 1 3.4 0l8.4 14a2 2 0 0 1-1.7 3H3.6a2 2 0 0 1-1.7-3zM12 9v4M12 17h.01",
  lock: "M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4",
  logout: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9",
  sun: "M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4",
  moon: "M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z",
  cog: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-2.82 1.17V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 7.3 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 2.6 14.7H2.5a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.2 7.3M14.7 2.6a1.65 1.65 0 0 0 1.82-.33",
  trash: "M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2",
  key: "M21 2l-2 2m-7.6 7.6a5 5 0 1 0-1.4 1.4l5-5L21 2M15 9l2 2",
  refresh: "M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5",
  sliders: "M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6",
  building: "M3 21h18M5 21V7l8-4v18M19 21V11l-6-3M9 9h.01M9 12h.01M9 15h.01M9 18h.01",
  search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3",
  calendar: "M3 4h18v18H3zM16 2v4M8 2v4M3 10h18",
  eye: "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  plug: "M9 2v6M15 2v6M6 8h12v4a6 6 0 0 1-12 0zM12 18v4",
  list: "M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01",
  bell: "M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0",
  cloud: "M18 10h-1.3A7 7 0 1 0 4 16h13a4 4 0 0 0 1-7.9z",
  cloudOff: "M22.6 16.5A4 4 0 0 0 18 10h-1.3a7 7 0 0 0-9.4-4.6M5 5l14 14M1 1l22 22",
  upload: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12",
  inbox: "M22 12h-6l-2 3h-4l-2-3H2M5.5 5h13l3.5 7v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6z",
  // chevrons for the profile chip + group menus
  chevronUp: "M18 15l-6-6-6 6",
  // nav-layout segmented toggle (side rail vs top bar)
  navSide: "M3 4h18v16H3zM9 4v16",
  navTop: "M3 4h18v16H3zM3 9h18",
  // collapse / expand the side rail (double chevrons)
  collapse: "M11 17l-5-5 5-5M18 17l-5-5 5-5",
  expand: "M13 17l5-5-5-5M6 17l5-5-5-5",
  user: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
  // WO timer controls
  play: "M6 4l14 8-14 8z",
  stopSquare: "M6 6h12v12H6z",
  // topbar language dropdown
  globe: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z",
} as const;

export function Icon({
  d,
  size = 18,
  sw = 2,
  fill = "none",
  style,
}: {
  d: string;
  size?: number;
  sw?: number;
  fill?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={fill}
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

export type BtnVariant = "primary" | "secondary" | "ghost" | "go" | "stop";

export function Btn({
  children,
  variant = "secondary",
  size = "core",
  disabled,
  onClick,
  type = "button",
  className = "",
  ...rest
}: {
  children: ReactNode;
  variant?: BtnVariant;
  size?: "core" | "hero" | "sm";
  disabled?: boolean;
  onClick?: () => void;
  type?: "button" | "submit";
  className?: string;
} & Record<string, unknown>) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={`btn btn-${variant} btn-${size} ${className}`.trim()}
      {...rest}
    >
      {children}
    </button>
  );
}

export function StatusChip({ kind, children }: { kind: StatusKind; children: ReactNode }) {
  return (
    <span className={`chip-status ${kind}`}>
      <span className="led" />
      {children}
    </span>
  );
}

/**
 * Relative-overdue bar — fill = late ÷ interval (capped), band from the core
 * `relativeOverdue` rule. Reused on the work board + device-card maintenance.
 */
export function OverdueBar({ late, interval }: { late: number; interval: number }) {
  if (interval <= 0) return null;
  const { percent, band } = relativeOverdue(late, interval);
  const kind = band === "alarm" ? "crit" : band === "warn" ? "warn" : "ok";
  return (
    <span className={`overdue-bar ${kind}`} title={`${late} late of ${interval} interval (${percent}%)`}>
      <span className="overdue-seg" style={{ width: `${Math.max(6, percent)}%` }} />
    </span>
  );
}

/** A measurement point (LL/L/H/HH) + the current value, for the ThresholdBar. */
export interface ThresholdPoint {
  name: string;
  unit?: string;
  ll: number;
  l: number;
  h: number;
  hh: number;
  value: number;
}

/**
 * The LL/L/H/HH tolerance band visualisation (ported from the prototype
 * measurement engine). The band is classified via the SAME core
 * `classifyTolerance` rule the server + DeviceCard use — single source of truth,
 * no parallel implementation. Reused by the Device-Card Measurements tab, the
 * Measurement-templates point editor, and the Meter-readings screen.
 */
export function ThresholdBar({ p }: { p: ThresholdPoint }) {
  const t = useScopedT();
  const min = p.ll - (p.hh - p.ll) * 0.15;
  const max = p.hh + (p.hh - p.ll) * 0.15;
  const span = max - min || 1;
  const pos = (x: number) => Math.max(0, Math.min(100, ((x - min) / span) * 100));
  const { band } = classifyTolerance({ ll: p.ll, l: p.l, h: p.h, hh: p.hh }, p.value);
  const cls = band === "alarm" ? "crit" : band === "warn" ? "warn" : "ok";
  return (
    <div className="mp">
      <div className="mp-head">
        <span className="mp-name">{p.name}</span>
        <span className={`mp-val ${cls}`}>
          {p.value}
          <small>{p.unit}</small>
        </span>
      </div>
      <div className="mp-bar">
        <span className="mp-zone alarm" style={{ left: 0, width: `${pos(p.ll)}%` }} />
        <span className="mp-zone warn" style={{ left: `${pos(p.ll)}%`, width: `${pos(p.l) - pos(p.ll)}%` }} />
        <span className="mp-zone ok" style={{ left: `${pos(p.l)}%`, width: `${pos(p.h) - pos(p.l)}%` }} />
        <span className="mp-zone warn" style={{ left: `${pos(p.h)}%`, width: `${pos(p.hh) - pos(p.h)}%` }} />
        <span className="mp-zone alarm" style={{ left: `${pos(p.hh)}%`, right: 0 }} />
        <span className="mp-marker" style={{ left: `${pos(p.value)}%` }} />
      </div>
      <div className="mp-scale">
        <span>{p.ll}</span>
        <span>{p.l}</span>
        <span style={{ color: "var(--status-success-ink)", fontWeight: 700 }}>{t("dc.measure.normal")}</span>
        <span>{p.h}</span>
        <span>{p.hh}</span>
      </div>
    </div>
  );
}

const SYNC_BADGE: Record<RecordSyncState, { label: string; icon: string; cls: string; title: string }> = {
  synced: { label: "Synced", icon: ICONS.cloud, cls: "synced", title: "Synced with the server" },
  pending: {
    label: "Sync pending",
    icon: ICONS.upload,
    cls: "pending",
    title: "A local change is queued and will sync when online",
  },
  cached_offline: {
    label: "Cached offline",
    icon: ICONS.cloudOff,
    cls: "offline",
    title: "Showing locally cached data — you are offline",
  },
};

/**
 * Per-record sync badge (CACHED OFFLINE / SYNC PENDING / Synced). Driven by the
 * SyncContext record state (connectivity + the local mutation queue). `compact`
 * shows the icon only (for dense grids); otherwise icon + label.
 */
export function SyncBadge({ state, compact = false }: { state: RecordSyncState; compact?: boolean }) {
  // The "synced" state is the quiet default — only show it when explicitly asked
  // (non-compact), so grids aren't noisy when everything is fine.
  const b = SYNC_BADGE[state];
  return (
    <span className={`sync-badge ${b.cls}`} title={b.title} role="status" data-sync-state={state}>
      <Icon d={b.icon} size={12} sw={2.2} />
      {!compact && <span>{b.label}</span>}
    </span>
  );
}
